import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronRight, SlidersHorizontal, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export const localDayKey = (iso) => {
  const date = new Date(iso);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const ProfileLiveSchedule = ({ lives, testPrefix = 'profile-schedule' }) => {
  const { t, i18n } = useTranslation('lives');
  const [date, setDate] = useState('');
  const [period, setPeriod] = useState('all');
  const [filters, setFilters] = useState(false);
  const [limit, setLimit] = useState(3);
  const scheduled = lives.filter((l) => ['scheduled', 'waiting_tracks'].includes(l.status))
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const filtered = scheduled.filter((l) => {
    const hour = new Date(l.scheduled_at).getHours();
    return (!date || localDayKey(l.scheduled_at) === date) && (period === 'all'
      || (period === 'morning' && hour >= 6 && hour < 12)
      || (period === 'afternoon' && hour >= 12 && hour < 18)
      || (period === 'evening' && hour >= 18) || (period === 'night' && hour < 6));
  });
  if (!scheduled.length) return null;
  const reset = () => { setDate(''); setPeriod('all'); setLimit(3); };
  return (
    <section className="mt-6 min-w-0 border-y border-amber-500/25 py-5" data-testid={testPrefix}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold md:text-lg" data-testid={`${testPrefix}-heading`}>
          <CalendarDays className="h-5 w-5 shrink-0 gold-text" />{t('schedule.heading')}
          <span className="text-sm text-muted-foreground" data-testid={`${testPrefix}-count`}>{scheduled.length}</span>
        </h2>
        <Button size="icon" variant="ghost" aria-expanded={filters} aria-label={t('schedule.filters')} title={t('schedule.filters')}
          onClick={() => setFilters(!filters)} data-testid={`${testPrefix}-filters-toggle`}>
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </div>
      {filters && <div className="mb-4 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`${testPrefix}-filters`}>
        <label className="min-w-0 text-xs text-muted-foreground" data-testid={`${testPrefix}-date-label`}>
          {t('schedule.date')}
          <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setLimit(3); }}
            className="mt-1 min-w-0 max-w-full" data-testid={`${testPrefix}-date`} />
        </label>
        <label className="min-w-0 text-xs text-muted-foreground" data-testid={`${testPrefix}-period-label`}>
          {t('schedule.period')}
          <select value={period} onChange={(e) => { setPeriod(e.target.value); setLimit(3); }}
            className="mt-1 h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm text-foreground" data-testid={`${testPrefix}-period`}>
            {['all', 'morning', 'afternoon', 'evening', 'night'].map((p) => <option key={p} value={p}>{t(`schedule.${p}`)}</option>)}
          </select>
        </label>
        {(date || period !== 'all') && <Button variant="ghost" size="sm" onClick={reset} data-testid={`${testPrefix}-reset`}>{t('schedule.reset')}</Button>}
      </div>}
      <ul className="divide-y divide-border" data-testid={`${testPrefix}-list`}>
        {filtered.slice(0, limit).map((live) => {
          const when = new Date(live.scheduled_at);
          const full = live.max_participants && live.tickets_sold >= live.max_participants;
          return <li key={live.id} data-testid={`${testPrefix}-item-${live.id}`}>
            <Link to={`/lives/${live.id}`} className="group flex min-w-0 items-center gap-3 py-3 transition-colors hover:bg-secondary/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-400"
              data-testid={`${testPrefix}-open-${live.id}`}>
              <time dateTime={live.scheduled_at} className="flex w-14 shrink-0 flex-col items-center border-r border-amber-500/30 pr-3 gold-text" data-testid={`${testPrefix}-date-${live.id}`}>
                <span className="text-xl font-semibold leading-none">{when.getDate()}</span>
                <span className="mt-1 text-xs">{when.toLocaleDateString(i18n.language, { month: 'short' })}</span>
              </time>
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-semibold" data-testid={`${testPrefix}-title-${live.id}`}>{live.title}</p>
                <p className="mt-1 text-xs text-muted-foreground" data-testid={`${testPrefix}-time-${live.id}`}>{when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} · {live.duration_minutes} min · {live.is_paid ? t('schedule.credits', { count: live.price }) : t('schedule.free')}</p>
                <p className="mt-1 flex items-center gap-1 text-xs gold-text" data-testid={`${testPrefix}-booking-${live.id}`}>
                  {live.is_booked && !live.is_owner && <Check className="h-3 w-3" />}
                  {t(live.status === 'waiting_tracks' ? 'schedule.starting' : live.is_owner ? 'schedule.manage' : live.is_booked ? 'schedule.booked' : full ? 'schedule.full' : 'schedule.book')}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 gold-text transition-transform group-hover:translate-x-0.5" />
            </Link>
          </li>;
        })}
      </ul>
      {!filtered.length && <p className="py-4 text-sm text-muted-foreground" data-testid={`${testPrefix}-empty`}>{t('schedule.empty')}</p>}
      {filtered.length > limit && <Button variant="ghost" className="mt-2 w-full gold-text" onClick={() => setLimit(limit + 6)} data-testid={`${testPrefix}-more`}>
        <ChevronDown className="mr-2 h-4 w-4" />{t('schedule.show_more', { count: filtered.length - limit })}
      </Button>}
      {limit > 3 && <Button variant="ghost" className="mt-2 w-full" onClick={() => setLimit(3)} data-testid={`${testPrefix}-less`}>{t('schedule.show_less')}</Button>}
    </section>
  );
};